/**
 * The schema-driven config form, shared by the Scrapers tab (#35-#38) and the Comment Generation
 * tab (#77-#79).
 *
 * Both render a `ScraperFieldDTO[]` a plugin/backend declared for itself, under identical rules —
 * which is the point of the plugin pattern: a newly installed scraper *or* AI backend is
 * configurable without touching a component. Two of those rules are server-enforced and mirrored
 * here so the UI never lies:
 *
 *  - a secret is **write-only**: the input starts empty, the stored mask shows beside it, and an
 *    untouched field is simply not sent (omitted-means-keep; `''` clears);
 *  - a 400 answers with `details: [{key, message}]`, and each message belongs beside its own field
 *    rather than in one form-level line (§11.1).
 *
 * Extracted rather than copied: the two cards differ only in their heads and their mutations, and a
 * drift between them would be a drift in how secrets are handled.
 */

import type { ConfigFieldType, ScraperFieldDTO } from '@lw/shared';

/** Only the fields the user actually touched are in here — the rest are omitted from the write. */
export type ConfigDraft = Record<string, string>;

function inputType(type: ConfigFieldType): string {
  if (type === 'secret') return 'password';
  if (type === 'number') return 'number';
  return 'text';
}

/** Draft strings back to the runtime types the plugin declared. */
function coerce(type: ConfigFieldType, raw: string): string | number | boolean {
  if (type === 'number') return Number(raw);
  if (type === 'boolean') return raw === 'true';
  return raw;
}

function currentString(field: ScraperFieldDTO): string {
  if (field.type === 'secret') return '';
  if (field.value === null || field.value === undefined) return '';
  return String(field.value);
}

/** The #36/#78 body's `config`: the touched fields only, each in its declared runtime type. */
export function configFromDraft(
  fields: ScraperFieldDTO[],
  draft: ConfigDraft,
): Record<string, string | number | boolean> {
  const config: Record<string, string | number | boolean> = {};
  for (const [key, raw] of Object.entries(draft)) {
    const field = fields.find((candidate) => candidate.key === key);
    if (field === undefined) continue;
    config[key] = coerce(field.type, raw);
  }
  return config;
}

/** `details: [{key, message}]` from a 400 body, flattened for inline rendering (§11.1). */
export function fieldErrorsOf(error: unknown): Record<string, string> {
  const details = (error as { details?: unknown } | null)?.details;
  if (!Array.isArray(details)) return {};
  const errors: Record<string, string> = {};
  for (const entry of details) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { key, message } = entry as { key?: unknown; message?: unknown };
    if (typeof key === 'string' && typeof message === 'string') errors[key] = message;
  }
  return errors;
}

export type ConfigFieldsGridProps = {
  /** Namespaces the input ids, so two cards on one tab cannot collide on a shared field key. */
  idPrefix: string;
  fields: ScraperFieldDTO[];
  draft: ConfigDraft;
  onChange: (key: string, value: string) => void;
  /** Inline messages by field key, from `fieldErrorsOf`. */
  errors: Record<string, string>;
  /** Clears one stored secret (`''` through the same config write). */
  onClearSecret: (key: string) => void;
  /** A save is in flight — only the Remove buttons need to know. */
  busy: boolean;
};

export function ConfigFieldsGrid({
  idPrefix,
  fields,
  draft,
  onChange,
  errors,
  onClearSecret,
  busy,
}: ConfigFieldsGridProps) {
  return (
    <div className="settings-grid">
      {fields.map((field) => {
        const id = `${idPrefix}-${field.key}`;
        const value = draft[field.key] ?? currentString(field);
        const error = errors[field.key];
        return (
          <div className="field" key={field.key}>
            <label className="field__label" htmlFor={id}>
              {field.label}
              {field.required === false ? <span className="faint"> (optional)</span> : null}
            </label>

            {field.type === 'enum' ? (
              <select
                id={id}
                className="select"
                value={value}
                onChange={(event) => onChange(field.key, event.target.value)}
              >
                {(field.options ?? []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : field.type === 'boolean' ? (
              <label className="toggle">
                <input
                  id={id}
                  type="checkbox"
                  checked={value === 'true'}
                  onChange={(event) => onChange(field.key, String(event.target.checked))}
                />
                <span>Enabled</span>
              </label>
            ) : (
              <input
                id={id}
                className="input"
                type={inputType(field.type)}
                autoComplete="off"
                placeholder={field.type === 'secret' ? 'Enter a new value to replace' : ''}
                value={value}
                onChange={(event) => onChange(field.key, event.target.value)}
              />
            )}

            {field.type === 'secret' ? (
              <span className="row muted settings-secret">
                {field.isSet === true ? `Stored: ${field.masked ?? '••••'}` : 'Not set'}
                {' · leave blank to keep it'}
                {field.isSet === true ? (
                  <button
                    type="button"
                    className="btn-ghost btn-ghost--sm"
                    onClick={() => onClearSecret(field.key)}
                    disabled={busy}
                  >
                    Remove
                  </button>
                ) : null}
              </span>
            ) : null}
            {error !== undefined ? (
              <span className="settings-bad">{error}</span>
            ) : field.help !== undefined ? (
              <span className="field__hint">{field.help}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default ConfigFieldsGrid;
